import React, { useCallback, useEffect, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useAssessment } from '../context/AssessmentContext';
import { useAuth } from '../context/AuthContext';

const InstructionsPage = () => {
  const history = useHistory();
  const { profile } = useAuth();
  const { assessmentId, selfIntroSubmitted, startAssessment } = useAssessment();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(false);
  const [permissionNotice, setPermissionNotice] = useState('Requesting camera and microphone access…');
  const [permissionError, setPermissionError] = useState(null);

  useEffect(() => {
    let activeStream = null;
    let cancelled = false;

    const requestPermissions = async () => {
      if (
        typeof navigator === 'undefined' ||
        !navigator.mediaDevices ||
        typeof navigator.mediaDevices.getUserMedia !== 'function'
      ) {
        setPermissionNotice(null);
        setPermissionError('Camera and microphone permissions are not supported in this browser.');
        return;
      }
      try {
        activeStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (cancelled) {
          return;
        }
        setPermissionError(null);
        setPermissionNotice(null);
      } catch (mediaError) {
        if (!cancelled) {
          setPermissionNotice(null);
          setPermissionError('Camera and microphone access is required. Please allow permissions above and reload.');
        }
      } finally {
        if (activeStream) {
          activeStream.getTracks().forEach((track) => track.stop());
          activeStream = null;
        }
      }
    };

    requestPermissions();

    return () => {
      cancelled = true;
      if (activeStream) {
        activeStream.getTracks().forEach((track) => track.stop());
        activeStream = null;
      }
    };
  }, []);

  const handleStart = useCallback(async () => {
    setStarting(true);
    setError(null);
    try {
      await startAssessment({ snapshots: [] });
      history.push('/self-intro');
    } catch (err) {
      const message =
        err?.response?.data?.detail?.message || err?.message || 'Unable to start the assessment.';
      if (err?.response?.status === 409) {
        setLocked(true);
        setError(message);
      } else {
        setError(message);
      }
      setStarting(false);
    }
  }, [history, startAssessment]);

  if (assessmentId) {
    return <Redirect to={selfIntroSubmitted ? '/exam' : '/self-intro'} />;
  }

  return (
    <div className="instructions-page">
      <div className="instructions-card">
        <h1>Assessment Instructions</h1>
        <p>
          Welcome {profile?.email || 'candidate'}. Please review the following instructions before
          beginning your assessment.
        </p>
        <ol>
          <li>Ensure you are alone in a quiet environment with reliable internet.</li>
          <li>Allow camera permissions when prompted. Your webcam will remain active.</li>
          <li>Stay in fullscreen mode and avoid switching tabs to prevent AI proctoring penalties.</li>
          <li>Answer each section in order. Some sections are timed and cannot be revisited.</li>
          <li>You can request help from support via chat if technical issues occur.</li>
        </ol>
        {permissionNotice && <div className="instructions-info">{permissionNotice}</div>}
        {permissionError && <div className="instructions-error">{permissionError}</div>}
        {error && <div className="instructions-error">{error}</div>}
        <button type="button" onClick={handleStart} disabled={starting || locked}>
          {locked ? 'Assessment already completed' : starting ? 'Preparing assessment…' : 'Begin Assessment'}
        </button>
      </div>
    </div>
  );
};

export default InstructionsPage;
